import { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import {
  Receipt,
  Search,
  DollarSign,
  Clock,
  CheckCircle,
  AlertCircle,
  ChevronDown,
  ChevronUp,
  Download,
  ExternalLink,
  Loader
} from 'lucide-react'
import api from '../config/api'
import { formatDate } from '../utils/formatDate'
import '../styles/MyInvoices.css'

function MyInvoices() {
  const [invoices, setInvoices] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [filter, setFilter] = useState('all')
  const [searchTerm, setSearchTerm] = useState('')
  const [expandedInvoice, setExpandedInvoice] = useState(null)
  const [payingInvoiceId, setPayingInvoiceId] = useState(null)

  useEffect(() => {
    fetchInvoices()
  }, [filter])

  const fetchInvoices = async () => {
    try {
      setLoading(true)
      const params = filter !== 'all' ? { status: filter } : {}
      const response = await api.get('/invoices', { params })
      setInvoices(response.data.data)
    } catch (err) {
      setError('Failed to load invoices')
    } finally {
      setLoading(false)
    }
  }

  const handlePayNow = async (invoiceId) => {
    try {
      setPayingInvoiceId(invoiceId)
      setError('')

      const response = await api.post('/checkout/create-session', { invoiceId })

      if (response.data.success && response.data.data.checkoutUrl) {
        window.location.href = response.data.data.checkoutUrl
      } else {
        setError('Failed to initiate payment. Please try again.')
      }
    } catch (err) {
      const message = err.response?.data?.message || 'Failed to initiate payment. Please try again.'
      setError(message)
    } finally {
      setPayingInvoiceId(null)
    }
  }

  const filteredInvoices = invoices.filter(invoice =>
    invoice.invoiceNumber?.toLowerCase().includes(searchTerm.toLowerCase())
  )

  const getStatusIcon = (status) => {
    switch (status) {
      case 'paid': return CheckCircle
      case 'pending': return Clock
      case 'overdue': return AlertCircle
      default: return Receipt
    }
  }

  const getStatusClass = (status) => {
    switch (status) {
      case 'paid': return 'status-paid'
      case 'pending': return 'status-pending'
      case 'overdue': return 'status-overdue'
      default: return ''
    }
  }

  const totalOutstanding = invoices
    .filter(inv => inv.status !== 'paid')
    .reduce((sum, inv) => sum + (inv.balance || inv.amount || 0), 0)

  return (
    <div className="my-invoices-page">
      <header className="page-header">
        <div className="header-content">
          <Receipt size={28} />
          <div>
            <h1>Invoices</h1>
            <p>View and manage your billing</p>
          </div>
        </div>
      </header>

      {/* Summary Card */}
      <div className="invoices-summary">
        <div className="summary-card">
          <DollarSign size={24} />
          <div>
            <span className="summary-label">Total Outstanding</span>
            <span className="summary-value">{totalOutstanding.toLocaleString('en-PH', { style: 'currency', currency: 'PHP' })}</span>
          </div>
        </div>
        <div className="summary-stats">
          <div className="stat">
            <span className="stat-value">{invoices.filter(i => i.status === 'pending').length}</span>
            <span className="stat-label">Pending</span>
          </div>
          <div className="stat overdue">
            <span className="stat-value">{invoices.filter(i => i.status === 'overdue').length}</span>
            <span className="stat-label">Overdue</span>
          </div>
          <div className="stat paid">
            <span className="stat-value">{invoices.filter(i => i.status === 'paid').length}</span>
            <span className="stat-label">Paid</span>
          </div>
        </div>
      </div>

      {/* Controls */}
      <div className="invoices-controls">
        <div className="search-box">
          <Search size={18} />
          <input
            type="text"
            placeholder="Search by invoice number..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <div className="filter-pills">
          {['all', 'pending', 'overdue', 'paid'].map((status) => (
            <button
              key={status}
              className={`filter-pill ${filter === status ? 'active' : ''}`}
              onClick={() => setFilter(status)}
            >
              {status.charAt(0).toUpperCase() + status.slice(1)}
            </button>
          ))}
        </div>
      </div>

      {error && (
        <div className="alert alert-error">
          <AlertCircle size={18} />
          <span>{error}</span>
        </div>
      )}

      {loading ? (
        <div className="loading-container">
          <div className="loading-spinner" />
          <p>Loading invoices...</p>
        </div>
      ) : filteredInvoices.length === 0 ? (
        <div className="empty-state">
          <Receipt size={48} />
          <h3>No invoices found</h3>
          <p>
            {searchTerm
              ? 'Try adjusting your search'
              : 'You don\'t have any invoices yet'}
          </p>
        </div>
      ) : (
        <div className="invoices-list">
          {filteredInvoices.map((invoice, index) => {
            const StatusIcon = getStatusIcon(invoice.status)
            const isExpanded = expandedInvoice === invoice.id
            const isUnpaid = invoice.status !== 'paid'
            const isPaying = payingInvoiceId === invoice.id

            return (
              <motion.div
                key={invoice.id}
                className={`invoice-card ${getStatusClass(invoice.status)}`}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
              >
                <div
                  className="invoice-header"
                  onClick={() => setExpandedInvoice(isExpanded ? null : invoice.id)}
                >
                  <div className="invoice-main">
                    <StatusIcon size={20} className="status-icon" />
                    <div className="invoice-info">
                      <span className="invoice-number">{invoice.invoiceNumber}</span>
                      <span className="invoice-date">
                        Due: {invoice.dueDate ? formatDate(invoice.dueDate) : 'N/A'}
                      </span>
                    </div>
                  </div>
                  <div className="invoice-amount">
                    <span className="amount">{invoice.amount?.toLocaleString('en-PH', { style: 'currency', currency: 'PHP' })}</span>
                    <span className={`status-badge ${getStatusClass(invoice.status)}`}>
                      {invoice.status}
                    </span>
                  </div>
                  {isUnpaid && (
                    <button
                      className="pay-now-btn compact"
                      onClick={(e) => {
                        e.stopPropagation()
                        handlePayNow(invoice.id)
                      }}
                      disabled={isPaying}
                    >
                      {isPaying ? (
                        <Loader size={14} className="spinning" />
                      ) : (
                        'Pay Now'
                      )}
                    </button>
                  )}
                  <button className="expand-btn">
                    {isExpanded ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                  </button>
                </div>

                {isExpanded && (
                  <motion.div
                    className="invoice-details"
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                  >
                    {invoice.items?.length > 0 && (
                      <div className="invoice-items">
                        <h4>Line Items</h4>
                        <table>
                          <thead>
                            <tr>
                              <th>Description</th>
                              <th>Qty</th>
                              <th>Rate</th>
                              <th>Amount</th>
                            </tr>
                          </thead>
                          <tbody>
                            {invoice.items.map((item, i) => (
                              <tr key={i}>
                                <td>{item.description}</td>
                                <td>{item.quantity}</td>
                                <td>{Number(item.rate).toLocaleString('en-PH', { style: 'currency', currency: 'PHP' })}</td>
                                <td>{(item.quantity * item.rate).toLocaleString('en-PH', { style: 'currency', currency: 'PHP' })}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                    <div className="invoice-footer">
                      <div className="invoice-totals">
                        <div className="total-row">
                          <span>Subtotal:</span>
                          <span>{invoice.amount?.toLocaleString('en-PH', { style: 'currency', currency: 'PHP' })}</span>
                        </div>
                        {invoice.balance !== invoice.amount && (
                          <div className="total-row balance">
                            <span>Balance Due:</span>
                            <span>{invoice.balance?.toLocaleString('en-PH', { style: 'currency', currency: 'PHP' })}</span>
                          </div>
                        )}
                      </div>
                      {isUnpaid && (
                        <button
                          className="pay-now-btn"
                          onClick={(e) => {
                            e.stopPropagation()
                            handlePayNow(invoice.id)
                          }}
                          disabled={isPaying}
                        >
                          {isPaying ? (
                            <>
                              <Loader size={16} className="spinning" />
                              Processing...
                            </>
                          ) : (
                            <>
                              <ExternalLink size={16} />
                              Pay Now
                            </>
                          )}
                        </button>
                      )}
                    </div>
                  </motion.div>
                )}
              </motion.div>
            )
          })}
        </div>
      )}
    </div>
  )
}

export default MyInvoices
